/**
 * Route Logger Utility
 * Logs all route navigation events for debugging routing issues
 */

interface RouteLogEntry {
  timestamp: number
  type: 'navigate' | 'location_change' | 'redirect' | 'push' | 'replace'
  from: string
  to: string
  method?: string
  search?: string
  state?: any
}

class RouteLogger {
  private logs: RouteLogEntry[] = []

  private maxLogs = 100

  private isEnabled = true

  constructor() {
    if (typeof window !== 'undefined') {
      // Log initial page load
      this.log({
        type: 'location_change',
        from: '',
        to: window.location.pathname,
        search: window.location.search,
      })

      // Intercept popstate events (browser back/forward)
      window.addEventListener('popstate', () => {
        this.log({
          type: 'location_change',
          from: this.logs[this.logs.length - 1]?.to || '',
          to: window.location.pathname,
          search: window.location.search,
        })
      })
    }
  }

  log(entry: Omit<RouteLogEntry, 'timestamp'>): void {
    if (!this.isEnabled) return

    const logEntry: RouteLogEntry = {
      ...entry,
      timestamp: Date.now(),
    }

    this.logs.push(logEntry)

    // Keep only the last maxLogs entries
    if (this.logs.length > this.maxLogs) {
      this.logs.shift()
    }
  }

  getLogs(): RouteLogEntry[] {
    return [...this.logs]
  }

  getRecentLogs(count: number = 10): RouteLogEntry[] {
    return this.logs.slice(-count)
  }

  clearLogs(): void {
    this.logs = []
  }

  enable(): void {
    this.isEnabled = true
  }

  disable(): void {
    this.isEnabled = false
  }
}

// Singleton instance
export const routeLogger = new RouteLogger()
